<template>
  <div id="VerifyUpdateProfileModal" class="modal modalFade userModels userResetPasswordModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <button type="button" class="backBtn">
            <i class="fi-rr-angle-right"></i>
          </button>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>
        <!-- Modal Body -->
        <div class="modal-body">
          <div class="icon_wrap">
            <svg id="user_linear" data-name="user/linear" xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24">
              <g id="vuesax_linear_user" data-name="vuesax/linear/user">
                <g id="user">
                  <path d="M10,5A5,5,0,1,1,5,0,5,5,0,0,1,10,5Z" transform="translate(7 2)" fill="none" stroke="#292d32" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"/>
                  <path d="M17.18,7c0-3.87-3.85-7-8.59-7S0,3.13,0,7" transform="translate(3.41 15)" fill="none" stroke="#292d32" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"/>
                  <path d="M0,0H24V24H0Z" fill="none" opacity="0"/>
                </g>
              </g>
          </svg>
          </div>
          <h3 class="icon_title">التحقق</h3>
          <p class="mt-3">
            أدخل كود التحقق المرسل إليك
            <span id="entered_new_mobile" dir="ltr">+966{{ authStore.mobile }}</span>
          </p>
          <form @submit.prevent="handleVerificationWrapper">
            <div class="d-flex verificationInput">
              <!-- Render 4 code inputs -->
              <input v-for="(digit, index) in codeDigits" :key="index"
                type="text" class="code-input code-input-v2" id="code-input-verfiy-v2" maxlength="1"
                pattern="[0-9]*" inputmode="numeric"
                v-model="codeDigits[index]" @input="codeDigits[index] = toEnglishNumber(codeDigits[index])" autofocus />
            </div>
            <span v-if="verificationError" class="has-error text-danger w-100 mt-2 mb-2">
              {{ verificationError }}
            </span>
            <div class="resend_code">
               <a href="javascript:void(0)" v-if="!canResend" @click="startCountdown" id="resetCodeBtn">
                إعادة ارسال خلال :
                 00:<span>{{ countdown }}</span>
               </a>
               <a href="javascript:void(0)" v-if="canResend" @click="resendCode" id="resendNewMobileBtn"
                 class="resend_link">
                 إعادة ارسال
               </a>
             </div>
           
            <div class="input-group mb-0">
              <button type="submit" class="form-control has-spinner-link">
                <div class="button_text">تحقق</div>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from "vue";
import { useBootstrapModal } from "@/composables/useBootstrapModal";
import { useAuth } from "@/composables/useAuth";
import { useAuthStore } from '@/store/auth';
import Swal from 'sweetalert2';

const { handleProfileVerification, handleResendCode } = useAuth();
const { closeModal } = useBootstrapModal("VerifyUpdateProfileModal");
const { showAlert } = useGlobalAlert();
const authStore = useAuthStore();

const digits = 4;
const codeDigits = ref<string[]>(Array(digits).fill(""));
const verificationError = ref("");
const countdown = ref(30);
const canResend = ref(false);
let countdownTimer: ReturnType<typeof setInterval> | null = null;

const startCountdown = () => {
  if (countdownTimer) {
    clearInterval(countdownTimer);
    countdownTimer = null;
  }
  countdown.value = 30;
  canResend.value = false;
  countdownTimer = setInterval(() => {
    countdown.value--;
    if (countdown.value <= 0) {
      canResend.value = true;
      clearInterval(countdownTimer!);
      countdownTimer = null;
    }
  }, 1000);
};

const toEnglishNumber = (value) => {
  return value.replace(/[٠-٩]/g, d => "٠١٢٣٤٥٦٧٨٩".indexOf(d));
};

const modalEl = ref<HTMLElement | null>(null);

onMounted(() => {
  modalEl.value = document.getElementById("VerifyUpdateProfileModal");
  if (modalEl.value) {
    modalEl.value.addEventListener("shown.bs.modal", startCountdown);
  }

  const modalElement = document.getElementById("VerifyUpdateProfileModal");
  if (modalElement) {
    modalElement.addEventListener("shown.bs.modal", () => {
      nextTick(() => {
        const input = document.getElementById("code-input-verfiy-v2") as HTMLInputElement;
        if (input) input.focus();
      });
    });
  }
});

onBeforeUnmount(() => {
  if (modalEl.value) {
    modalEl.value.removeEventListener("shown.bs.modal", startCountdown);
  }
});

const resendCode = async () => {
  const success = await handleResendCode(authStore.mobile);
  if (success) {
    startCountdown();
  } else {
    verificationError.value = "رمز التحقق غير صحيح";
  }
};

const handleVerificationWrapper = async () => {
  const code = codeDigits.value.map(toEnglishNumber).join("");
  if (code.length !== digits) {
    verificationError.value = "يرجى إدخال رمز التحقق";
    return;
  }
  const success = await handleProfileVerification(code);
  if (success) {
    closeModal();
    showAlert('تم التعديل بنجاح', 'success');
    setTimeout(() => {
      reloadNuxtApp();
    }, 2000);
  } else {
    verificationError.value = "رمز التحقق غير صحيح";
  }
};
// --- Auto-Tab Functionality ---
interface InputListener {
  element: HTMLInputElement;
  inputHandler: EventListener;
  keydownHandler: EventListener;
  pasteHandler: EventListener;
}

const inputListeners: InputListener[] = [];

const autoTab = () => {
  const inputs = document.querySelectorAll<HTMLInputElement>(".code-input-v2");

  inputs.forEach((input, index) => {
    const inputHandler = (event: Event) => {
      const target = event.target as HTMLInputElement;
      target.value = toEnglishNumber(target.value); // Convert input value
      if (target.value.length === 1 && index < inputs.length - 1) {
        (inputs[index + 1] as HTMLInputElement).focus();
      }
      checkCompletion();
    };

    const keydownHandler = (event: KeyboardEvent) => {
      const target = event.target as HTMLInputElement;
      if (event.key === "Backspace" && !target.value && index > 0) {
        (inputs[index - 1] as HTMLInputElement).focus();
      }
    };

    const pasteHandler = (event: ClipboardEvent) => {
      event.preventDefault();
      const pasteData = (event.clipboardData || window.clipboardData).getData("text").trim();
      if (!pasteData) return;

      let chars = toEnglishNumber(pasteData).split("");
      for (let i = 0; i < chars.length && index + i < inputs.length; i++) {
        (inputs[index + i] as HTMLInputElement).value = chars[i];
      }

      // Move focus to the last filled input
      const nextIndex = Math.min(index + chars.length, inputs.length - 1);
      (inputs[nextIndex] as HTMLInputElement).focus();

      checkCompletion();
    };

    input.addEventListener("input", inputHandler);
    input.addEventListener("keydown", keydownHandler);
    input.addEventListener("paste", pasteHandler);

    inputListeners.push({ element: input, inputHandler, keydownHandler, pasteHandler });
  });

  function checkCompletion() {
    const allFilled = Array.from(inputs).every((input) => input.value.length === 1);
    if (allFilled) {
      handleVerificationWrapper();
    }
  }
};

onMounted(() => {
  autoTab();
});

onBeforeUnmount(() => {
  inputListeners.forEach(({ element, inputHandler, keydownHandler, pasteHandler }) => {
    element.removeEventListener("input", inputHandler);
    element.removeEventListener("keydown", keydownHandler);
    element.removeEventListener("paste", pasteHandler);
  });
});


</script>
